import { Alert, Button, CircularProgress, Stack } from "@mui/material";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import React, { useEffect, useState } from "react";
import { gateway } from "../gateway";
import { getEnvVar } from "../utils/get-env";

const key = getEnvVar("REACT_APP_STRIPE_PUBLISHABLE_KEY") ?? process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ?? "";
const stripePromise = key ? loadStripe(key) : null;

const CardForm = ({ subscriptionId, onSuccess }: { subscriptionId: string; onSuccess: () => void }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!stripe || !elements) return;
    setSaving(true);
    setError("");
    try {
      const result = await stripe.confirmSetup({ elements, confirmParams: { return_url: window.location.href }, redirect: "if_required" });
      if (result.error) throw new Error(result.error.message || "The card could not be saved.");
      const paymentMethod = result.setupIntent?.payment_method;
      const paymentMethodId = typeof paymentMethod === "string" ? paymentMethod : paymentMethod?.id;
      if (result.setupIntent?.status !== "succeeded" || !paymentMethodId) throw new Error("The card was not confirmed.");
      await gateway.post(`/subscriptions/fixed/${subscriptionId}/payment-method`, { paymentMethodId });
      onSuccess();
    } catch (cause: any) {
      setError(cause?.response?.data?.message || cause?.message || "The card could not be updated.");
    } finally {
      setSaving(false);
    }
  };

  return <form onSubmit={submit}>
    <Stack spacing={2}>
      <PaymentElement />
      {error && <Alert severity="error">{error}</Alert>}
      <Button type="submit" variant="contained" disabled={!stripe || saving}>
        {saving ? <CircularProgress size={20} /> : "Save new card"}
      </Button>
    </Stack>
  </form>;
};

export const UpdateFixedPaymentMethod = ({ companyId, subscriptionId, onSuccess }: { companyId: number; subscriptionId: string; onSuccess: () => void }) => {
  const [secret, setSecret] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    gateway.post("/stripe/vault/setup-intent", { companyId })
      .then((response: any) => {
        const value = response?.data?.data?.clientSecret || response?.data?.clientSecret;
        if (!value) throw new Error("Card setup could not be started.");
        if (active) setSecret(value);
      })
      .catch((cause: any) => { if (active) setError(cause?.response?.data?.message || cause?.message || "Card setup could not be started."); });
    return () => { active = false; };
  }, [companyId]);

  if (!stripePromise) return <Alert severity="error">Card payments are unavailable.</Alert>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!secret) return <CircularProgress size={24} />;
  return <Elements stripe={stripePromise} options={{ clientSecret: secret }}><CardForm subscriptionId={subscriptionId} onSuccess={onSuccess} /></Elements>;
};
